/**
 * Application initialization utilities
 */
import { loadServerTranslations, SUPPORTED_LOCALES, type SupportedLocale } from './i18n'
import sessionManager from '../utils/sessionManager'
import configService from '../services/config.service'
import type { I18n } from 'vue-i18n'
import { useAuthStore, useUiStore } from '@/stores'

interface LocaleMessages {
  [key: string]: any
}

interface AllMessages {
  [locale: string]: LocaleMessages
}

/**
 * Initialize the Vue application with all necessary configurations
 * @param i18n - Vue i18n instance
 */
export async function initializePiniaApp(i18n: I18n): Promise<void> {
  try {
    // Load application configuration first
    console.log('🔧 Initializing application configuration...')
    await configService.loadConfig()
    console.log('✅ Configuration loaded successfully')
    
    // Load server-side translations and merge with client translations
    try {
      await loadServerTranslations(i18n)
      console.log('✅ Server translations loaded successfully')
    } catch (error) {
      console.warn('⚠️ Failed to load server translations:', error)
      // Continue initialization even if server translations fail
    }
    
    // Store merged translations in Pinia store for easier component access
    try {
      await storeTranslationsInPinia(i18n)
      console.log('✅ Translations stored in Pinia successfully')
    } catch (error) {
      console.warn('⚠️ Failed to store translations in Pinia:', error)
      // Continue initialization even if storing translations fails
    }
    
    // Check authentication status
    try {
      const authStore = useAuthStore()
      await authStore.checkAuth()
      console.log('✅ Authentication check completed')
      
      // Initialize session manager if authenticated
      if (authStore.isAuthenticated) {
        sessionManager.init()
        console.log('✅ Session manager initialized')
      }
    } catch (error) {
      console.warn('⚠️ Authentication check failed:', error)
      // Continue initialization even if auth check fails
    }
  } catch (error) {
    console.error('❌ Application initialization error:', error)
  }
}

/**
 * Store i18n translations in Pinia store
 * @param i18n - Vue i18n instance
 */
async function storeTranslationsInPinia(i18n: I18n): Promise<void> {
  // Check if i18n instance is properly initialized
  if (!i18n || !i18n.global || typeof i18n.global.getLocaleMessage !== 'function') {
    console.warn('i18n instance not properly initialized')
    return
  }
  
  try {
    const allMessages: AllMessages = {}
    SUPPORTED_LOCALES.forEach((locale: SupportedLocale) => {
      allMessages[locale] = i18n.global.getLocaleMessage(locale)
    })
    
    const uiStore = useUiStore()
    uiStore.setMessages(allMessages)
  } catch (error) {
    console.warn('Failed to store translations in Pinia:', error)
  }
}

/**
 * Setup session manager watcher
 */
export function setupPiniaSessionManager(): void {
  const authStore = useAuthStore()
  
  // Watch for authentication changes
  authStore.$subscribe((_mutation: any, state: any) => {
    if (state.isAuthenticated) {
      sessionManager.init()
    } else {
      sessionManager.cleanup()
    }
  })
  
  console.log('Session manager watcher setup complete')
}

// Legacy Vuex function - kept for backward compatibility during migration
export function setupSessionManager(): void {
  console.warn('setupSessionManager (Vuex) is deprecated, use setupPiniaSessionManager instead')
}
